import { DASHBOARD_ROUTES } from '../homeDashboard/dashboardRoutes';
import { CONTENT_ROUTES } from '../homeDashboard/contentRoutes';
import { QUIZ_ROUTES } from '../homeDashboard/quizzes/quizRoutes';
import { QUESTION_BANK_ROUTES } from '../questionBank/questionBankRoutes';
import { LECTURE_ROUTES } from '../homeDashboard/lecture/lectureRoutes';
import { RAPID_FIRE_ROUTES } from '../homeDashboard/rapidFire/rapidFireRoutes';
import { FLASH_CARD_ROUTES } from '../homeDashboard/flashcard/flashCardRoutes';
import { GAMIFICATION_ROUTES } from '../homeDashboard/gamification/gamificationRoutes';
import { EXAM_COURSE_ROUTES } from '../homeDashboard/courses/examCourseRoutes';
import { DOUBT_ROUTES } from '../homeDashboard/doubts/doubtRoutes';
import { EXPLORE_ROUTES } from '../homeDashboard/exploreRoutes';
import { SUBSCRIPTION_ROUTES } from '../homeDashboard/subscription/subscriptionRoutes';
import { SETTINGS_ROUTES } from './settingsRoutes';

const read = (item, ...keys) => keys.map((key) => item?.[key]).find((value) => value !== undefined && value !== null);
const positiveId = (value) => {
  const raw = String(value ?? '').trim();
  if (!/^\d+$/.test(raw)) return 0;
  const result = Number(raw);
  return Number.isSafeInteger(result) && result > 0 ? result : 0;
};

const localPaths = new Set([
  ...Object.values(DASHBOARD_ROUTES), ...Object.values(CONTENT_ROUTES),
  ...Object.values(QUIZ_ROUTES), ...Object.values(QUESTION_BANK_ROUTES),
  ...Object.values(LECTURE_ROUTES), ...Object.values(RAPID_FIRE_ROUTES),
  ...Object.values(FLASH_CARD_ROUTES), ...Object.values(GAMIFICATION_ROUTES),
  ...Object.values(EXAM_COURSE_ROUTES), ...Object.values(DOUBT_ROUTES),
  ...Object.values(EXPLORE_ROUTES), ...Object.values(SUBSCRIPTION_ROUTES),
  ...Object.values(SETTINGS_ROUTES),
]);

export function resolveNotificationDestination(item, { savedStateId = 0, savedStateName = '', origin = '' } = {}) {
  const rawUrl = String(read(item, 'url', 'notification_url') || '').trim();
  if (rawUrl) {
    const candidate = /^www\./i.test(rawUrl) ? `https://${rawUrl}` : rawUrl;
    try {
      const url = new URL(candidate, origin || undefined);
      if (url.protocol === 'https:' || url.protocol === 'http:') {
        if ((url.origin === origin || url.hostname === 'admin.vidyakasam.in') && localPaths.has(url.pathname)) {
          const payloadRoute = resolveNotificationDestination(
            { ...item, url: '', notification_url: '', vk_order_id: '', vkOrderId: '' },
            { savedStateId, savedStateName, origin }
          );
          return {
            kind: 'internal',
            path: `${url.pathname}${url.search}${url.hash}`,
            ...(payloadRoute.path === url.pathname ? { state: payloadRoute.state } : {}),
          };
        }
        return { kind: 'external', url: url.href };
      }
    } catch (_error) {
      // Invalid URLs fall through to the payment or category payload.
    }
  }

  const orderId = String(read(item, 'vk_order_id', 'vkOrderId') || '').trim();
  if (orderId) return { kind: 'internal', path: SUBSCRIPTION_ROUTES.paymentResult, state: { vkOrderId: orderId } };

  const rawCategory = read(item, 'quest_category_id', 'questCategoryId', 'category_id', 'categoryId');
  if (rawCategory === undefined || rawCategory === null || !/^\d+$/.test(String(rawCategory).trim())) {
    return { kind: 'internal', path: DASHBOARD_ROUTES.home };
  }
  const categoryId = Number(rawCategory);
  const stateId = positiveId(read(item, 'state_id', 'stateId')) || positiveId(savedStateId);
  const stateName = String(read(item, 'state_name', 'stateName') || savedStateName || '');
  const subjectId = positiveId(read(item, 'subject_id', 'subjectId'));
  const subjectName = String(read(item, 'subject_name', 'subjectName') || '');
  const categoryName = String(read(item, 'quest_category_name', 'questCategoryName', 'category_name') || '');
  const examId = positiveId(read(item, 'exam_id', 'examId'));
  const context = {
    isNotificationRoute: true, quizRoutes: 'notificationRoute',
    state_id: stateId, stateId, stateName,
    selectedState: { state_id: stateId, state_name: stateName },
    category_id: categoryId, categoryId, categoryName,
    selectedCategory: { id: categoryId, category_id: categoryId, category_name: categoryName },
    subject_id: subjectId, subjectId, subjectName,
    selectedSubject: { id: subjectId, name: subjectName },
    exam_id: examId, examId,
  };
  const route = (path, details = {}) => ({ kind: 'internal', path, state: { ...context, ...details } });

  switch (categoryId) {
    case 0: {
      const seriesId = positiveId(read(item, 'test_series_id', 'testSeriesId', 'series_id'));
      return seriesId ? route(QUIZ_ROUTES.testSeriesList, { series_id: seriesId, selectedSeries: { id: seriesId } }) : route(QUIZ_ROUTES.list);
    }
    case 1: {
      const quizId = positiveId(read(item, 'quiz_id', 'quizId'));
      return quizId ? route(QUIZ_ROUTES.start, { quiz_id: quizId, selectedQuiz: { id: quizId, quizId } }) : route(QUIZ_ROUTES.list);
    }
    case 2: {
      const questionId = positiveId(read(item, 'question_id', 'questionId'));
      return questionId && subjectId ? route(QUESTION_BANK_ROUTES.practice, { question_id: questionId, selectedQuestion: { id: questionId } }) : route(QUESTION_BANK_ROUTES.list);
    }
    case 3: {
      const lectureId = positiveId(read(item, 'lecture_id', 'lectureId'));
      return lectureId && subjectId ? route(LECTURE_ROUTES.detail, { lectures_id: lectureId, selectedLecture: { id: lectureId } }) : route(LECTURE_ROUTES.list);
    }
    case 4: {
      const noteId = positiveId(read(item, 'note_id', 'noteId'));
      return noteId && subjectId ? route(CONTENT_ROUTES.noteDetail, { note_id: noteId, selectedNote: { id: noteId } }) : route(CONTENT_ROUTES.notes);
    }
    case 5: return route(EXPLORE_ROUTES.subjects);
    case 6: {
      const battleId = positiveId(read(item, 'battles_id', 'battle_id', 'battleId'));
      if (!battleId) return route(GAMIFICATION_ROUTES.start);
      const details = { battles_id: battleId, selectedBattle: { id: battleId, battles_id: battleId } };
      if (subjectId && stateId && (battleId === 1 || battleId === 2)) {
        return route(battleId === 1 ? GAMIFICATION_ROUTES.groupWaiting : GAMIFICATION_ROUTES.singleWaiting, { ...details, entryMode: 'random' });
      }
      return route(EXPLORE_ROUTES.subjects, details);
    }
    case 7: return examId ? route(EXAM_COURSE_ROUTES.detail, { exams_id: examId, selectedExam: { id: examId } }) : route(EXAM_COURSE_ROUTES.masterExams);
    case 9: return route(EXPLORE_ROUTES.subjects);
    case 10: {
      const doubtId = positiveId(read(item, 'doubt_id', 'doubtId'));
      return doubtId ? route(DOUBT_ROUTES.detail, { doubt_id: doubtId, selectedDoubt: { id: doubtId } }) : route(DOUBT_ROUTES.list);
    }
    case 12: {
      const podcastId = positiveId(read(item, 'audio_podcast_id', 'audioPodcastId', 'podcast_id'));
      return podcastId && subjectId ? route(CONTENT_ROUTES.podcastDetail, { podcast_id: podcastId, selectedPodcast: { id: podcastId } }) : route(EXPLORE_ROUTES.subjects);
    }
    case 13: {
      const infographicId = positiveId(read(item, 'infographics_id', 'infographicsId', 'infographic_id'));
      return infographicId && subjectId ? route(CONTENT_ROUTES.infographicDetail, { infographic_id: infographicId, selectedInfographic: { id: infographicId } }) : route(EXPLORE_ROUTES.subjects);
    }
    default: return { kind: 'internal', path: DASHBOARD_ROUTES.home };
  }
}
